/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './earnings-account-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {createObjectTypeItemLink} from '../common/directives/adn-select/adn-select-item-link.util';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {attachEditPagePhase} from '../common/util/edit-page-phase';
import type {EditPagePhaseApi} from '../common/util/edit-page-phase';
import {compareById} from '../common/util/object-id-comparison';
import {
  applyFullPageSizesToModel,
  AX_PUBLISHER_CONTACT_USER_PERMISSIONS,
  expandCountries,
  FullPageDimensions,
  loadFullPageDimensionsFromModel,
  transparencyLanguageFromEarningsAccount
} from './earnings-account-commons';
import {
  buildApprovalNotificationTree,
  buildApprovalPreviewNotificationsFromHistoryItem,
  buildApprovalPreviewRowFromHistoryItem,
  canPreviewApprovalHistoryItem,
  getActionableApprovalCreatives,
  getApprovalHistoryApprovalClass,
  getApprovalHistoryApprovalLabel,
  getApprovalHistoryEntityType,
  getApprovalNotificationsForRow,
  getApprovalPayloadForHistoryItem,
  getApprovalPayloadForRow,
  getApprovalHistoryFilterRequest,
  getApprovalHistorySearchLabel,
  getApprovalPreviewHistoryQuery,
  getApprovalPreviewRow,
  getOppositeApprovalForHistoryItem,
  refreshApprovalNotificationRows,
  setApprovalNotificationRowAction,
  setApprovalNotificationRowsAction,
  APPROVAL_HISTORY_SEARCH_TYPES
} from './earnings-account-approval.util';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {SearchResource} from '../../components/api/resources/resource/search';
import {
  ApprovalPreviewCallbacks,
  SiteApprovalPreviewModalComponent
} from '../site/site-approval-preview-modal.component';
import {BlocklistResource} from '../../components/api/resources/resource/blocklist.resource';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {EarningsAccountResource} from '../../components/api/resources/resource/earnings-account.resource';
import {RoleResource} from '../../components/api/resources/resource/role.resource';
import {RulesetResource} from '../../components/api/resources/resource/ruleset.resource';
import {UserResource} from '../../components/api/resources/resource/user.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {WINDOW} from '../angular-platform-providers';

@Component({
  standalone: false,
  selector: 'adn-earnings-account-edit-page',
  template
})
export class EarningsAccountEditPageComponent extends AdminResourceEditPageBase {
  readonly rulesetItemLink = createObjectTypeItemLink('Ruleset');
  readonly blocklistItemLink = createObjectTypeItemLink('Blocklist');

  isAxPublisher = false;
  canEditAdvertisingAccess = false;
  pureMarketplacePlatform = false;
  isPoliticalAdsPlatform = false;
  showFullPageOptions = false;
  showCountries = false;
  networkCurrencies: string[] = [];

  teams: any[] = [];
  axPublisherContactUsers: any[] | undefined;
  blocklists: any[] | false = false;
  rulesets: any[] | false = false;
  siteRulesets: any[] = [];
  targetingRulesets: any[] = [];
  sizeRulesets: any[] = [];
  layoutRulesets: any[] = [];

  canManageApprovalNotifications = false;
  approvalNotifications: any[] = [];
  approvalNotificationTree: any[] = [];
  approvalNotificationRows: any[] = [];
  showApprovals = false;
  acknowledgingApprovalNotifications = false;
  approvingApprovalNotifications = false;
  acknowledgingAllApprovalNotifications = false;
  approvalNotificationsLoading = false;
  approvalNotificationsError = false;
  approvalHistory: any = {
    results: [],
    totalCount: 0,
    currentPage: 1,
    pageSize: 20,
    pageQuery: {$resolved: true},
    changePage: () => this.loadApprovalHistory()
  };
  approvalHistoryLoading = false;
  approvalHistoryError = false;
  approvalHistoryLoaded = false;
  approvalHistoryUpdating = false;
  approvalHistoryApprovalLevel: string = '';
  approvalHistoryFilter: any = null;
  approvalHistorySearchResults: any[] = [];
  readonly approvalLevelOptions = [
    {value: '', label: 'All object types'},
    {value: 'ADVERTISER', label: 'Advertisers'},
    {value: 'TEAM', label: 'Teams'},
    {value: 'ORDER', label: 'Orders'},
    {value: 'LINE_ITEM', label: 'Line Items'},
    {value: 'CREATIVE', label: 'Creatives'}
  ];

  fullPageDims: FullPageDimensions = {};

  /** Attached in constructor — template gates the form with [query]="editPhaseQuery". */
  editPhaseQuery: {$resolved: boolean} = {$resolved: false};
  private editPhaseApi: EditPagePhaseApi;

  private axPublisherRoleIdsPromise: Promise<string[]> | undefined;
  private usersPromise: Promise<any[]> | undefined;
  private contactUsersTeamId: string | undefined;
  private pendingAsyncLoads = 0;
  /** Sync re-entry guard; template uses the deferred public busy flags. */
  private approvalActionInFlight = false;
  /** Sync guards while loading flags are deferred for the template. */
  private approvalNotificationsLoadInFlight = false;
  private approvalHistoryLoadInFlight = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly translate: TranslateService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(BlocklistResource) private readonly Blocklist: any,
    @Inject(RulesetResource) private readonly Ruleset: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(UserResource) private readonly User: any,
    @Inject(RoleResource) private readonly Role: any,
    @Inject(EarningsAccountResource) private readonly EarningsAccount: any,
    @Inject(SearchResource) private readonly searchResource: any,
    @Inject(WINDOW) private readonly window: any,
    private readonly angularModalService: AngularModalService,
    private readonly cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);

    this.isAxPublisher = LocalUserPermissions.isAnyAxPublisher();
    this.canEditAdvertisingAccess = LocalUserPermissions.hasNetwork('MANAGE_BUSINESS')
      || LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');
    this.pureMarketplacePlatform = LocalNetworkProfile.isPureMarketplacePlatform();
    this.isPoliticalAdsPlatform = LocalNetworkProfile.isPoliticalAdsPlatform();

    const isSpecialUser = localUserProfile.get('username') === 'adnuntius@adnuntius.com'
      || localUserProfile.get('username') === 'broker1@adnuntius.com';
    const isLocalhost = this.window.location.hostname === 'localhost';
    this.showFullPageOptions = isLocalhost || (this.pureMarketplacePlatform && isSpecialUser);
    this.showCountries = LocalNetworkInfo.obtain().getSiteCountries().length > 1;

    const defaultCurrency = LocalNetworkProfile.get('defaultCurrency');
    this.networkCurrencies = _.orderBy(LocalNetworkProfile.get('availableCurrencies'), (curr: string) => {
      return curr === defaultCurrency ? 0 : 1;
    });

    this.deferFormDirtyTracking();
    this.editPhaseApi = attachEditPagePhase(this, this.cdr);
    this.editPhaseApi.wait('async');
    this.pendingAsyncLoads = this.pureMarketplacePlatform ? 3 : 2;
    this.loadBlocklists();
    if (this.pureMarketplacePlatform) {
      this.loadRulesets();
    }
    this.loadTeams();
  }

  get showPublisherTransparencyFields(): boolean {
    return (this.LocalNetworkProfile.hasPoliticalCapability() || this.pureMarketplacePlatform)
      && this.editingModel?.politicalAdvertising !== 'BLOCKED';
  }

  get transparencyPreviewUrl(): string {
    const publisherId = _.get(this.editingModel, 'id');
    const networkTag = this.LocalNetworkProfile.getNetworkTag();
    if (!publisherId || !networkTag) {
      return '';
    }
    let url = 'https://adtransparency.eu/transparency-preview?n='
      + encodeURIComponent(networkTag)
      + '&pe=' + encodeURIComponent(publisherId)
      + '&preview=true&political=true';
    const language = transparencyLanguageFromEarningsAccount(this.editingModel);
    if (language) {
      url += '&lang=' + encodeURIComponent(language);
    }
    return url;
  }

  getApprovalObjectTypePrefix = (type: string): string => {
    return this.getApprovalTypeLabel({type}) + ': ';
  };

  getApprovalTypeLabel(row: any): string {
    if (!row || !row.type) {
      return 'Object';
    }
    return this.translate.instant(row.type + '.' + row.type);
  }

  getApprovalHistoryApprovalLabel = getApprovalHistoryApprovalLabel;
  getApprovalHistoryApprovalClass = getApprovalHistoryApprovalClass;
  canPreviewApprovalHistoryItem = canPreviewApprovalHistoryItem;

  get showApprovalHistoryFilters(): boolean {
    if (this.approvalHistoryFilter || this.approvalHistoryApprovalLevel) {
      return true;
    }
    return (this.approvalHistory.totalCount || 0) > (this.approvalHistory.pageSize || 20);
  }

  getApprovalHistoryPendingMessage(historyItem: any): string {
    const typeLabel = this.getApprovalTypeLabel({type: _.get(historyItem, 'entityType')});
    return 'The ' + typeLabel + ' will be available for review again once it becomes a candidate for delivery';
  }

  shouldShowApprovalHistoryToggle(): boolean {
    if (!this.canManageApprovalNotifications) {
      return false;
    }
    if (this.editingModel.politicalAdvertising === 'REQUIRES_APPROVAL') {
      return true;
    }
    if (this.approvalHistoryError) {
      return true;
    }
    if (!this.approvalHistoryLoaded) {
      return false;
    }
    return (this.approvalHistory.totalCount || 0) > 0;
  }

  teamChange(): void {
    this.initialiseAxPublisherContactUsers(false);
    this.markExternalFormDirty();
  }

  toggleApprovalNotificationGroup(group: any): void {
    if (!group || _.isEmpty(group.children)) {
      return;
    }
    group.expanded = !group.expanded;
    this.approvalNotificationRows = refreshApprovalNotificationRows(this.approvalNotificationTree);
  }

  rebuildApprovalNotificationTree(): void {
    this.approvalNotificationTree = buildApprovalNotificationTree(this.approvalNotifications, this.approvalNotificationTree);
    this.approvalNotificationRows = refreshApprovalNotificationRows(this.approvalNotificationTree);
  }

  loadApprovalNotifications(): void {
    if (!this.canManageApprovalNotifications || !this.editingModel?.id || this.approvalNotificationsLoadInFlight) {
      return;
    }
    this.approvalNotificationsLoadInFlight = true;
    // Defer — Refresh [disabled] binds approvalNotificationsLoading (NG0100 false→true).
    deferMarkForCheck(this.cdr, () => {
      this.approvalNotificationsLoading = true;
      this.approvalNotificationsError = false;
    });
    this.EarningsAccount.getApprovalNotifications({id: this.editingModel.id}).$promise.then((response: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalNotifications = response.needsApproval || response.creatives || [];
        this.rebuildApprovalNotificationTree();
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalNotificationsError = true;
      });
    }).finally(() => {
      this.approvalNotificationsLoadInFlight = false;
      deferMarkForCheck(this.cdr, () => {
        this.approvalNotificationsLoading = false;
      });
    });
  }

  onApprovalLevelChange(level: string): void {
    this.approvalHistoryApprovalLevel = level;
    this.reloadApprovalHistory();
  }

  onApprovalHistoryFilterChange(): void {
    this.reloadApprovalHistory();
  }

  clearApprovalHistoryFilters(): void {
    if (!this.approvalHistoryFilter && !this.approvalHistoryApprovalLevel) {
      return;
    }
    this.approvalHistoryFilter = null;
    this.approvalHistoryApprovalLevel = '';
    this.reloadApprovalHistory();
  }

  searchApprovalHistoryObjects = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({
      q: searchElement.search,
      types: APPROVAL_HISTORY_SEARCH_TYPES,
      pageSize: 50
    }).then((data: any) => {
      deferMarkForCheck(this.cdr, () => {
        const results = _.map(data.searchResults || [], (result: any) => {
          result.displayName = getApprovalHistorySearchLabel(result);
          return result;
        });
        const selected = this.approvalHistoryFilter;
        if (selected?.id && !_.some(results, ['id', selected.id])) {
          if (!selected.displayName) {
            selected.displayName = getApprovalHistorySearchLabel(selected);
          }
          results.unshift(selected);
        }
        this.approvalHistorySearchResults = results;
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalHistorySearchResults = [];
      });
    });
  };

  loadApprovalHistory(): void {
    if (!this.canManageApprovalNotifications || !this.editingModel?.id || this.approvalHistoryLoadInFlight) {
      return;
    }
    this.approvalHistoryLoadInFlight = true;
    // Defer — Refresh [disabled] binds approvalHistoryLoading (NG0100 false→true).
    deferMarkForCheck(this.cdr, () => {
      this.approvalHistoryLoading = true;
      this.approvalHistoryError = false;
      this.approvalHistory.pageQuery.$resolved = false;
    });
    const historyParams: any = {
      id: this.editingModel.id,
      page: this.approvalHistory.currentPage || 1,
      pageSize: this.approvalHistory.pageSize || 20
    };
    if (this.approvalHistoryApprovalLevel) {
      historyParams.approvalLevel = this.approvalHistoryApprovalLevel;
    }
    this.EarningsAccount.getApprovalHistory(historyParams, getApprovalHistoryFilterRequest(this.approvalHistoryFilter)).$promise.then((response: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalHistory.results = _.map(response.results || [], (historyItem: any) => {
          historyItem.entityType = getApprovalHistoryEntityType(historyItem);
          return historyItem;
        });
        this.approvalHistory.totalCount = response.totalCount || 0;
        this.approvalHistoryLoaded = true;
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalHistoryError = true;
      });
    }).finally(() => {
      this.approvalHistoryLoadInFlight = false;
      deferMarkForCheck(this.cdr, () => {
        this.approvalHistoryLoading = false;
        this.approvalHistory.pageQuery.$resolved = true;
      });
    });
  }

  openApprovalPreview(row: any): void {
    if (row && row.entityType && !row.type) {
      const historyPreviewQuery = getApprovalPreviewHistoryQuery(row);
      if (!historyPreviewQuery) {
        return this.openApprovalPreviewModal(
          getApprovalPreviewRow(row),
          buildApprovalPreviewNotificationsFromHistoryItem(row)
        );
      }
      return this.Creative.query(historyPreviewQuery).$promise.then((page: any) => {
        const creatives = _.get(page, 'results') || [];
        return this.openApprovalPreviewModal(
          buildApprovalPreviewRowFromHistoryItem(row, creatives),
          buildApprovalPreviewNotificationsFromHistoryItem(row, creatives)
        );
      });
    }
    const previewRow = getApprovalPreviewRow(row);
    const previewNotifications = getApprovalNotificationsForRow(previewRow);
    return this.openApprovalPreviewModal(previewRow, previewNotifications);
  }

  updateApprovalHistoryItem(historyItem: any): void {
    const approval = getOppositeApprovalForHistoryItem(historyItem);
    const approvalPayload = getApprovalPayloadForHistoryItem(historyItem, approval as string);
    if (!approvalPayload || this.approvalHistoryUpdating || this.isApprovalActionBusy()) {
      return;
    }
    this.approvalHistoryUpdating = true;
    this.approvalHistoryError = false;
    historyItem.updating = true;
    this.EarningsAccount.recordApproval({id: this.editingModel.id}, approvalPayload).$promise.then(() => {
      this.loadApprovalNotifications();
      this.loadApprovalHistory();
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalHistoryError = true;
      });
    }).finally(() => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalHistoryUpdating = false;
        historyItem.updating = false;
      });
    });
  }

  acknowledgeApprovalNotifications(row: any): void {
    const creatives = getActionableApprovalCreatives(getApprovalNotificationsForRow(row));
    if (_.isEmpty(creatives) || this.isApprovalActionBusy()) {
      return;
    }
    this.approvalActionInFlight = true;
    this.setApprovalBusyFlag('acknowledgingApprovalNotifications', true, () => {
      setApprovalNotificationRowAction(row, creatives, 'acknowledging', true);
    });
    this.approvalNotificationsError = false;
    this.EarningsAccount.acknowledgeApprovalNotifications({id: this.editingModel.id}, {
      creatives: _.map(creatives, (creative) => ({id: creative.id}))
    }).$promise.then(() => {
      // Defer tree rebuild — Review [disabled] depends on acknowledgeableCreativeCount.
      deferMarkForCheck(this.cdr, () => {
        this.removeApprovalNotificationsForCreatives(creatives);
        this.rebuildApprovalNotificationTree();
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalNotificationsError = true;
      });
    }).finally(() => {
      this.approvalActionInFlight = false;
      this.setApprovalBusyFlag('acknowledgingApprovalNotifications', false, () => {
        setApprovalNotificationRowAction(row, creatives, 'acknowledging', false);
      });
    });
  }

  approveApprovalNotifications(row: any, approval: string, creativesOverride?: any[]): any {
    const creatives = getActionableApprovalCreatives(
      _.isArray(creativesOverride) ? creativesOverride : getApprovalNotificationsForRow(row)
    );
    const approvalPayload = getApprovalPayloadForRow(row, approval);
    if (!approvalPayload || _.isEmpty(creatives) || this.isApprovalActionBusy()) {
      return null;
    }
    this.approvalActionInFlight = true;
    this.setApprovalBusyFlag('approvingApprovalNotifications', true, () => {
      setApprovalNotificationRowAction(row, creatives, 'approving', true);
    });
    this.approvalNotificationsError = false;
    return this.EarningsAccount.recordApproval({id: this.editingModel.id}, approvalPayload).$promise.then(() => {
      if (approval === 'PENDING') {
        this.loadApprovalNotifications();
      } else {
        deferMarkForCheck(this.cdr, () => {
          this.removeApprovalNotificationsForCreatives(creatives);
          this.rebuildApprovalNotificationTree();
        });
      }
      return {approvedCreativeIds: _.map(creatives, 'id')};
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalNotificationsError = true;
      });
      return Promise.reject();
    }).finally(() => {
      this.approvalActionInFlight = false;
      this.setApprovalBusyFlag('approvingApprovalNotifications', false, () => {
        setApprovalNotificationRowAction(row, creatives, 'approving', false);
      });
    });
  }

  approveCreativeApprovalNotifications(rows: any[], approval: string): any {
    const creativeRows = _.filter(rows || [], (row) => {
      return !!_.get(row, 'creative.id') && !!getApprovalPayloadForRow(row, approval);
    });
    if (_.isEmpty(creativeRows) || this.isApprovalActionBusy()) {
      return null;
    }
    this.approvalActionInFlight = true;
    this.approvalNotificationsError = false;
    this.setApprovalBusyFlag('approvingApprovalNotifications', true, () => {
      setApprovalNotificationRowsAction(creativeRows, 'approving', true);
    });

    const approvedCreatives: any[] = [];
    const failures: any[] = [];

    const processNext = (index: number): any => {
      if (index >= creativeRows.length) {
        return failures.length ? Promise.reject({
          approvedCreativeIds: _.map(approvedCreatives, 'id'),
          message: 'Updated ' + approvedCreatives.length + ' of ' + creativeRows.length + ' individual creative' + (creativeRows.length === 1 ? '' : 's') + '.'
        }) : Promise.resolve({
          approvedCreativeIds: _.map(approvedCreatives, 'id')
        });
      }
      const creativeRow = creativeRows[index];
      const payload = getApprovalPayloadForRow(creativeRow, approval);
      return this.EarningsAccount.recordApproval({id: this.editingModel.id}, payload).$promise.then(() => {
        approvedCreatives.push(creativeRow.creative);
      }, () => {
        failures.push(creativeRow);
      }).then(() => processNext(index + 1));
    };

    return processNext(0).then((result: any) => {
      if (approval === 'PENDING') {
        this.loadApprovalNotifications();
      } else {
        deferMarkForCheck(this.cdr, () => {
          this.removeApprovalNotificationsForCreatives(approvedCreatives);
          this.rebuildApprovalNotificationTree();
        });
      }
      return result;
    }, (result: any) => {
      if (approval === 'PENDING') {
        this.loadApprovalNotifications();
      } else {
        deferMarkForCheck(this.cdr, () => {
          this.removeApprovalNotificationsForCreatives(approvedCreatives);
          this.rebuildApprovalNotificationTree();
        });
      }
      deferMarkForCheck(this.cdr, () => {
        this.approvalNotificationsError = failures.length > 0;
      });
      return Promise.reject(result);
    }).finally(() => {
      this.approvalActionInFlight = false;
      this.setApprovalBusyFlag('approvingApprovalNotifications', false, () => {
        setApprovalNotificationRowsAction(creativeRows, 'approving', false);
      });
    });
  }

  acknowledgeAllApprovalNotifications(): void {
    const creatives = getActionableApprovalCreatives(this.approvalNotifications);
    if (_.isEmpty(creatives) || this.isApprovalActionBusy()) {
      return;
    }
    this.approvalActionInFlight = true;
    this.setApprovalBusyFlag('acknowledgingAllApprovalNotifications', true);
    this.approvalNotificationsError = false;
    this.EarningsAccount.acknowledgeApprovalNotifications({id: this.editingModel.id}, {
      creatives: _.map(creatives, (creative) => ({id: creative.id}))
    }).$promise.then(() => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalNotifications = [];
        this.rebuildApprovalNotificationTree();
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.approvalNotificationsError = true;
      });
    }).finally(() => {
      this.approvalActionInFlight = false;
      this.setApprovalBusyFlag('acknowledgingAllApprovalNotifications', false);
    });
  }

  protected afterInitEditingModel(): void {
    this.editingModel.address = this.editingModel.address || {country: ''};
    this.editingModel.contactUsers = this.editingModel.contactUsers || [];
    this.editingModel.filterTeamsType = this.editingModel.filterTeamsType || 'NONE';
    if (!this.editingModel.politicalAdvertising) {
      this.editingModel.politicalAdvertising = 'ALLOWED';
    }
    this.editingModel.countriesExpanded = expandCountries(this.editingModel);
    this.initApprovalSettings();
    this.applyFullPageDimsFromModel();
    this.initialiseAxPublisherContactUsers(true);
    this.resolveTeamReference();
    this.resolveReferences();
  }

  protected afterInitAfterSave(saveObj?: any): void {
    if (!this.editingModel.politicalAdvertising) {
      this.editingModel.politicalAdvertising = 'ALLOWED';
    }
    this.editingModel.countriesExpanded = expandCountries(this.editingModel);
    this.applyFullPageDimsFromModel();
    this.initialiseAxPublisherContactUsers(true);
    this.resolveTeamReference();
    this.resolveReferences();
    void saveObj;
  }

  protected onBeforeSubmit(): void {
    if (!_.get(this.editingModel, ['team', 'id'])) {
      this.editingModel.contactUsers = [];
    } else if (this.axPublisherContactUsers) {
      this.sanitiseContactUsers();
    }
    if (this.editingModel.politicalAdvertising === 'ALLOWED') {
      this.editingModel.politicalAdvertising = null;
    }
    applyFullPageSizesToModel(this.editingModel, this.fullPageDims);
  }

  onTeamModelChange(): void {
    this.teamChange();
  }

  readonly compareById = compareById;

  onPoliticalAdvertisingChange(): void {
    this.markExternalFormDirty();
  }

  onFullPageDimChange(): void {
    this.markExternalFormDirty();
  }

  countryChangeHook = (): void => {
    this.markExternalFormDirty();
  };

  trackByApprovalKey(_index: number, item: any): string {
    return item.key;
  }

  private reloadApprovalHistory(): void {
    this.approvalHistory.currentPage = 1;
    this.approvalHistoryLoadInFlight = false;
    this.loadApprovalHistory();
  }

  private initApprovalSettings(): void {
    const isLocalhost = this.window.location.hostname === 'localhost';
    const politicalBlockingEnabled = this.editingModel.politicalAdvertising !== 'ALLOWED';
    this.canManageApprovalNotifications = (politicalBlockingEnabled || this.pureMarketplacePlatform
      || this.LocalNetworkProfile.isExperimental() || isLocalhost)
      && !this.isNew
      && (this.LocalUserPermissions.hasAny('MANAGE_PUBLISHING') || this.LocalUserPermissions.hasAny('AX_PUBLISHER'));

    if (this.canManageApprovalNotifications && this.editingModel?.id) {
      this.loadApprovalNotifications();
      this.loadApprovalHistory();
    }
  }

  private openApprovalPreviewModal(previewRow: any, previewNotifications: any[]): void {
    const creatives = getActionableApprovalCreatives(previewNotifications);
    if (_.isEmpty(creatives)) {
      return;
    }
    const callbacks: ApprovalPreviewCallbacks = {
      approveApprovalNotifications: (row, approval, override) => this.approveApprovalNotifications(row, approval, override),
      approveCreativeApprovalNotifications: (rows, approval) => this.approveCreativeApprovalNotifications(rows, approval),
      getApprovalObjectTypePrefix: (type) => this.getApprovalObjectTypePrefix(type),
      getApprovalTypeLabel: (row) => this.getApprovalTypeLabel(row)
    };
    const modalRef = this.angularModalService.show(SiteApprovalPreviewModalComponent, {
      class: 'modal-lg',
      initialState: {
        row: previewRow,
        previewNotifications,
        callbacks,
        onClose: () => {
          this.loadApprovalNotifications();
          this.loadApprovalHistory();
        }
      }
    });
    void modalRef;
  }

  private removeApprovalNotificationsForCreatives(creatives: any[]): void {
    const creativeIds = _.keyBy(creatives, 'id');
    _.remove(this.approvalNotifications, (existingNotification) => {
      const creative = _.has(existingNotification, 'creative')
        ? _.get(existingNotification, 'creative')
        : existingNotification;
      return !!creativeIds[_.get(creative, 'id')];
    });
  }

  private getCurrentTeamId(): string | undefined {
    return _.get(this.editingModel, ['team', 'id']);
  }

  private isApprovalActionBusy(): boolean {
    return this.approvalActionInFlight
      || this.approvingApprovalNotifications
      || this.acknowledgingApprovalNotifications
      || this.acknowledgingAllApprovalNotifications;
  }

  /**
   * Approval busy flags bind into Review button [disabled] on this page while
   * modal click handlers are still inside CD. Defer the flip to avoid NG0100.
   */
  private setApprovalBusyFlag(
    flag: 'approvingApprovalNotifications' | 'acknowledgingApprovalNotifications' | 'acknowledgingAllApprovalNotifications',
    value: boolean,
    beside?: () => void
  ): void {
    deferMarkForCheck(this.cdr, () => {
      this[flag] = value;
      if (beside) {
        beside();
      }
    });
  }

  private getAxPublisherRoleIds(): Promise<string[]> {
    if (!this.axPublisherRoleIdsPromise) {
      this.axPublisherRoleIdsPromise = this.Role.query().$promise.then((rolePage: any) => {
        return _.map(_.filter(rolePage.results, (role: any) => {
          return _.some(_.get(role, 'permissions', []), (permission: string) => {
            return AX_PUBLISHER_CONTACT_USER_PERMISSIONS.indexOf(permission) > -1;
          });
        }), 'id');
      }, () => []);
    }
    return this.axPublisherRoleIdsPromise;
  }

  private getUsers(): Promise<any[]> {
    if (!this.usersPromise) {
      this.usersPromise = this.User.query({
        team: this.getCurrentTeamId(),
        permissions: AX_PUBLISHER_CONTACT_USER_PERMISSIONS,
        concise: true,
        pageSize: LocalNetworkInfo.obtain().getInfo('userCount') + 50
      }).$promise.then((userPage: any) => userPage.results || [], () => []);
    }
    return this.usersPromise;
  }

  private hasAxPublisherTeamRole(user: any, publisherRoleIds: string[], teamId: string): boolean {
    const networkId = this.LocalNetworkProfile.getNetworkId();
    const rolesByNetwork = _.find(_.get(user, ['userRoles', 'rolesByNetwork'], []), (rolesForNetwork: any) => {
      return _.get(rolesForNetwork, ['network', 'id']) === networkId
        || _.get(rolesForNetwork, ['network', 'tagId']) === networkId;
    });
    return !!_.find(_.get(rolesByNetwork, 'roles', []), (roleEntry: any) => {
      return _.get(roleEntry, ['team', 'id']) === teamId
        && publisherRoleIds.indexOf(_.get(roleEntry, ['role', 'id'])) > -1;
    });
  }

  private sanitiseContactUsers(): void {
    // Keep selection as the same object instances from [items] so adn-select can
    // show chips/labels after the async user list loads.
    const selectedIds = _.keyBy(this.editingModel.contactUsers || [], 'id');
    this.editingModel.contactUsers = _.filter(this.axPublisherContactUsers || [], (user: any) => {
      return !!selectedIds[user.id];
    });
  }

  private initialiseAxPublisherContactUsers(preserveSelection: boolean): void {
    const teamId = this.getCurrentTeamId();
    if (this.contactUsersTeamId !== teamId) {
      this.usersPromise = undefined;
      this.contactUsersTeamId = teamId;
    }
    this.axPublisherContactUsers = undefined;
    if (!teamId) {
      this.axPublisherContactUsers = [];
      this.editingModel.contactUsers = [];
      return;
    }
    if (!preserveSelection) {
      this.editingModel.contactUsers = [];
    }
    this.getAxPublisherRoleIds().then((publisherRoleIds) => {
      return this.getUsers().then((users) => {
        return _.orderBy(_.filter(users, (user) => {
          return this.hasAxPublisherTeamRole(user, publisherRoleIds, teamId as string);
        }), [(user) => user.displayName || user.name || user.username]);
      });
    }).then((users) => {
      deferMarkForCheck(this.cdr, () => {
        if (this.getCurrentTeamId() !== teamId) {
          return;
        }
        this.axPublisherContactUsers = users;
        this.sanitiseContactUsers();
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        if (this.getCurrentTeamId() !== teamId) {
          return;
        }
        this.axPublisherContactUsers = [];
        this.editingModel.contactUsers = [];
      });
    });
  }

  private applyFullPageDimsFromModel(): void {
    this.fullPageDims = loadFullPageDimensionsFromModel(this.editingModel);
  }

  private loadBlocklists(): void {
    this.Blocklist.query({minimal: true}, (page: any) => {
      // false → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.blocklists = page.results || [];
        this.resolveBlocklistReference();
        this.onAsyncLoadComplete();
      });
    }, () => {
      // Leave [] (not false) so nested [resolved]="blocklists" spinners settle.
      deferMarkForCheck(this.cdr, () => {
        this.blocklists = [];
        this.onAsyncLoadComplete();
      });
    });
  }

  private loadRulesets(): void {
    this.Ruleset.query({minimal: false}, (page: any) => {
      deferMarkForCheck(this.cdr, () => {
        const results = page.results || [];
        this.rulesets = results;
        this.siteRulesets = _.filter(results, (rs: any) => rs.type === 'INVENTORY');
        this.targetingRulesets = _.filter(results, (rs: any) => rs.type === 'TARGETING');
        this.sizeRulesets = _.filter(results, (rs: any) => rs.type === 'SIZE');
        this.layoutRulesets = _.filter(results, (rs: any) => rs.type === 'LAYOUT');
        this.resolveRulesetReferences();
        this.onAsyncLoadComplete();
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.rulesets = [];
        this.siteRulesets = [];
        this.targetingRulesets = [];
        this.sizeRulesets = [];
        this.layoutRulesets = [];
        this.onAsyncLoadComplete();
      });
    });
  }

  private loadTeams(): void {
    this.Team.query({where: 'type in STANDARD,AX_PUBLISHER'}, (page: any) => {
      // [] → results flips *ngFor on the team <select> mid-CD (NG0100).
      deferMarkForCheck(this.cdr, () => {
        const allTeams = page.results || [];
        const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
        this.teams = isViewAllObjects ? allTeams : _.filter(allTeams, (g: any) => {
          return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
        });
        if (this.isAxPublisher && allTeams.length > 0 && this.teams.length > 0 && !this.editingModel.team) {
          this.editingModel.team = this.teams[0];
          this.initialiseAxPublisherContactUsers(true);
        }
        this.resolveTeamReference();
        this.initialiseAxPublisherContactUsers(true);
        this.onAsyncLoadComplete();
      });
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.teams = [];
        this.onAsyncLoadComplete();
      });
    });
  }

  private onAsyncLoadComplete(): void {
    this.pendingAsyncLoads--;
    if (this.pendingAsyncLoads <= 0) {
      this.editPhaseApi.done('async');
    }
  }

  private resolveReferences(): void {
    this.resolveBlocklistReference();
    this.resolveRulesetReferences();
  }

  private resolveTeamReference(): void {
    if (!this.editingModel?.team || !this.teams.length) {
      return;
    }
    const teamId = _.isString(this.editingModel.team)
      ? this.editingModel.team
      : this.editingModel.team.id;
    const match = _.find(this.teams, ['id', teamId]);
    if (match) {
      this.editingModel.team = match;
    } else if (!_.isString(this.editingModel.team)) {
      this.teams.push(this.editingModel.team);
    }
  }

  private resolveBlocklistReference(): void {
    if (!_.isArray(this.blocklists) || !this.editingModel?.blocklist?.id) {
      return;
    }
    const match = _.find(this.blocklists as any[], ['id', this.editingModel.blocklist.id]);
    if (match) {
      this.editingModel.blocklist = match;
    }
  }

  private resolveRulesetReferences(): void {
    if (!_.isArray(this.rulesets) || !this.editingModel?.siteRuleset?.id) {
      return;
    }
    const siteRuleset = _.find(this.rulesets as any[], ['id', this.editingModel.siteRuleset.id]);
    if (siteRuleset) {
      this.editingModel.siteRuleset = siteRuleset;
    }
  }
}
